import { useCallback, useRef } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';

interface OnboardingData {
  [key: string]: unknown;
}

export function useOnboardingPersistence() {
  const { toast } = useToast();
  const saveTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const lastSaveRef = useRef<string>('');

  const saveProgress = useCallback(async (
    email: string,
    currentStep: string,
    accountType: string,
    onboardingData: OnboardingData,
    sessionToken?: string
  ) => {
    if (!email) return;

    // Create a hash of the data to avoid saving duplicates
    const dataHash = JSON.stringify({ currentStep, accountType, onboardingData });
    if (dataHash === lastSaveRef.current) return;
    
    // Debounce saves
    if (saveTimeoutRef.current) {
      clearTimeout(saveTimeoutRef.current);
    }

    saveTimeoutRef.current = setTimeout(async () => {
      try {
        const { error } = await supabase.functions.invoke('save-onboarding-progress', {
          body: {
            email,
            session_token: sessionToken,
            current_step: currentStep,
            account_type: accountType,
            onboarding_data: onboardingData
          }
        });

        if (error) {
          console.error('Failed to save progress:', error);
        } else {
          lastSaveRef.current = dataHash;
          console.log('Progress auto-saved');
        }
      } catch (err) {
        console.error('Error saving progress:', err);
      }
    }, 1000); // Debounce by 1 second
  }, []);

  const sendOTP = useCallback(async (email: string) => {
    try {
      const { data, error } = await supabase.functions.invoke('send-onboarding-otp', {
        body: { email }
      });

      if (error) {
        console.error('Edge function error:', error);
        toast({
          title: 'Error',
          description: 'Failed to send verification code',
          variant: 'destructive'
        });
        return null;
      }

      // Check for application-level errors (e.g., no session found)
      if (data?.error) {
        toast({
          title: 'Not found',
          description: data.error,
          variant: 'destructive'
        });
        return null;
      }

      toast({
        title: 'Code sent',
        description: 'Demo mode: Use code 123456'
      });

      // For demo purposes, return the OTP
      return data?.demo_otp || '123456';
    } catch (err) {
      console.error('Error sending OTP:', err);
      toast({
        title: 'Error',
        description: 'Failed to send verification code',
        variant: 'destructive'
      });
      return null;
    }
  }, [toast]);

  const verifyOTP = useCallback(async (email: string, otp: string) => {
    try {
      const { data, error } = await supabase.functions.invoke('verify-onboarding-otp', {
        body: { email, otp }
      });

      if (error || data?.error) {
        toast({
          title: 'Verification failed',
          description: data?.error || 'Invalid or expired code',
          variant: 'destructive'
        });
        return null;
      }

      toast({
        title: 'Welcome back!',
        description: 'Your progress has been restored'
      });

      return data;
    } catch (err) {
      console.error('Error verifying OTP:', err);
      toast({
        title: 'Error',
        description: 'Failed to verify code',
        variant: 'destructive'
      });
      return null;
    }
  }, [toast]);

  const verifyPassword = useCallback(async (email: string, password: string, accountType?: string) => {
    try {
      const { data, error } = await supabase.functions.invoke('verify-onboarding-password', {
        body: { email, password, account_type: accountType }
      });

      // Check for application-level errors first (these come in data even with HTTP errors)
      if (data?.error || data?.under_review) {
        // Return the error so the caller can display it appropriately
        return { error: data.error, multiple_accounts: data.multiple_accounts, available_types: data.available_types, under_review: data.under_review };
      }

      if (error) {
        toast({
          title: 'Login failed',
          description: 'Unable to verify credentials. Please try again.',
          variant: 'destructive'
        });
        return { error: 'Unable to verify credentials. Please try again.' };
      }

      // Check if multiple accounts exist
      if (data?.multiple_accounts) {
        return { multiple_accounts: true, available_types: data.available_types };
      }

      toast({
        title: 'Welcome back!',
        description: 'Your progress has been restored'
      });

      return data;
    } catch (err) {
      console.error('Error verifying password:', err);
      toast({
        title: 'Error',
        description: 'Failed to verify credentials',
        variant: 'destructive'
      });
      return { error: 'Failed to verify credentials' };
    }
  }, [toast]);

  return {
    saveProgress,
    sendOTP,
    verifyOTP,
    verifyPassword
  };
}
